import React from 'react';
import styles from '../../styles/CreativeEditor.module.css';
import SlideshowIcon from '@mui/icons-material/Slideshow';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import CircularProgress from '@mui/material/CircularProgress';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

interface ViewerHeaderProps {
  // Left side
  onBack: () => void;
  title: string;
  isPublished: boolean;

  // Job badges (iteration mode)
  activeJobsCount?: number;
  successJobsCount?: number;
  failedJobsCount?: number;
  jobIndicator?: React.ReactNode;

  // Present / slide view
  showPresent?: boolean;
  isPresentActive?: boolean;
  isGeneratingSlides?: boolean;
  slideCountdown?: number | null;
  onTogglePresent?: () => void;

  // Download PPTX
  showDownloadPptx?: boolean;
  isDownloadingPptx?: boolean;
  onDownloadPptx?: () => void;

  // Publish/unpublish
  canPublish?: boolean;
  canUnpublish?: boolean;
  isPublishing?: boolean;
  publishDisabledReason?: string;
  publishActionDisabled?: boolean;
  onPublish?: () => void;
  onUnpublish?: () => void;

  // Delete
  canDelete?: boolean;
  onDelete?: () => void;

  // Version history
  showVersionHistory?: boolean;
  iterationNumber?: number;
  isActiveIteration?: boolean;
  onOpenVersionHistory?: () => void;

  // Mode
  editorMode: 'iteration' | 'item';
}

const ViewerHeader: React.FC<ViewerHeaderProps> = ({
  onBack,
  title,
  isPublished,
  activeJobsCount = 0,
  successJobsCount = 0,
  failedJobsCount = 0,
  jobIndicator,
  showPresent,
  isPresentActive,
  isGeneratingSlides,
  slideCountdown,
  onTogglePresent,
  showDownloadPptx,
  isDownloadingPptx,
  onDownloadPptx,
  canPublish,
  canUnpublish,
  isPublishing,
  publishActionDisabled,
  publishDisabledReason,
  onPublish,
  onUnpublish,
  canDelete,
  onDelete,
  showVersionHistory,
  iterationNumber,
  isActiveIteration,
  onOpenVersionHistory,
  editorMode,
}) => {
  const isItemMode = editorMode === 'item';
  return (
    <div className={styles.viewerHeader}>
      {/* Left side */}
      <div className={styles.viewerHeaderLeft}>
        <button className={styles.headerBackButton} onClick={onBack} aria-label="Back">
          <svg focusable="false" aria-hidden="true" viewBox="0 0 24 24" width="20" height="20">
            <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" fill="currentColor" />
          </svg>
        </button>
        <span className={styles.viewerHeaderTitle} title={title}>
          {title}
        </span>
      </div>

      {/* Right side */}
      <div className={styles.viewerHeaderRight}>
        {/* Version history */}
        {showVersionHistory && onOpenVersionHistory && iterationNumber !== undefined && (
          <button
            className={`${styles.headerTextButton} ${isActiveIteration ? styles.headerTextButtonActive : ''}`}
            onClick={onOpenVersionHistory}
            title="View version history"
          >
            Version {iterationNumber}
          </button>
        )}

        {/* PowerPoint */}
        {showPresent && onTogglePresent && (
          <button
            className={styles.headerPrimaryButton}
            onClick={onTogglePresent}
            disabled={isGeneratingSlides}
            title={isGeneratingSlides ? 'Generating slides...' : 'Open PowerPoint view'}
          >
            {isGeneratingSlides ? (
              <CircularProgress size={14} color="inherit" />
            ) : (
              <SlideshowIcon style={{ fontSize: 16 }} />
            )}
            {isGeneratingSlides
              ? (slideCountdown === 0 ? 'Any moment' : `~${slideCountdown}s`)
              : 'PowerPoint'}
          </button>
        )}

        {/* Publish/Unpublish */}
        {canPublish && !isPublished && onPublish && (
          <button
            className={styles.headerPrimaryButton}
            onClick={onPublish}
            disabled={publishActionDisabled}
            title={publishDisabledReason || 'Publish'}
          >
            {isPublishing ? <CircularProgress size={14} color="inherit" /> : <VisibilityIcon style={{ fontSize: 16 }} />}
            Publish
          </button>
        )}
        {canUnpublish && isPublished && onUnpublish && (
          <button
            className={styles.headerPrimaryButton}
            onClick={onUnpublish}
            title="Unpublish"
          >
            {isPublishing ? <CircularProgress size={14} color="inherit" /> : <VisibilityOffIcon style={{ fontSize: 16 }} />}
            Unpublish
          </button>
        )}

        {/* Delete */}
        {canDelete && onDelete && (
          <button
            className={styles.headerDangerButton}
            onClick={onDelete}
            title="Delete"
          >
            Delete
          </button>
        )}

        {/* Job badges */}
        {!isItemMode && (
          jobIndicator || (
            <div className={styles.jobBadgesContainer}>
              {activeJobsCount > 0 && (
                <span className={`${styles.jobBadge} ${styles.runningBadge}`}>
                  <AccessTimeIcon style={{ fontSize: 12, marginRight: 2 }} />
                  {activeJobsCount} running
                </span>
              )}
              {successJobsCount > 0 && (
                <span className={`${styles.jobBadge} ${styles.completedBadge}`}>
                  <CheckCircleOutlineIcon style={{ fontSize: 12, marginRight: 2 }} />
                  {successJobsCount} done
                </span>
              )}
              {failedJobsCount > 0 && (
                <span className={`${styles.jobBadge}`} style={{ backgroundColor: '#FEE6E6', color: '#D92D20' }}>
                  <ErrorOutlineIcon style={{ fontSize: 12, marginRight: 2 }} />
                  {failedJobsCount} failed
                </span>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
};

export default ViewerHeader;
